import TTLCache from '@isaacs/ttlcache';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchHolterEvents,
  fetchHolterRhythmEventsCount,
  formatEventStrip,
  generateFilterSpecialEventType,
  isAppliedFilterEvent,
} from '@/entities/event';
import { SelectionAreaWrapperHandle } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  canAvgRhythmEvents,
  canShowCountBeatEvents,
  canShowDurationEvents,
  EVENT_COUNT_CONVERT,
  KEY_CANCEL,
  KEY_RECOIL,
  STRIP_AFIB_THUMBNAIL_INFO,
} from '@/shared/config';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import {
  BEAT_HR_TAB_ID,
  selectEventChanges,
  selectEventFilter,
  selectHolterEvents,
  selectIsActiveTab,
  selectIsLoadingStrip,
  selectPageIndex,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import { getStripIndex, getStripPosition } from '@/entities/strip';
import { logError } from '@/shared/lib/apiHelpers';

import { useReadEventsInfoBeatHr } from '@/entities/beat';
import type { AfibStripEventsProps } from './afibStripEvents';

const afibStripEventsCache = new TTLCache({ ttl: 3600000, max: 3 });

/**
 * Backs the AFib strip thumbnail grid: loads one page of holter events, tracks which strip is selected and keeps a chunked cache warm. Serves a page from the TTL cache when its chunk is present, compensates the special-event count for events the pending changes remove, restores or resets the selected strip after each load, maps drag selection onto events, and walks the selection with the arrow keys, paging at either edge.
 *
 * @param props - the active rhythm event type plus the study, profile, timezone, report start and ECG data map the thumbnails need.
 * @returns An object carrying the loaded holter events, the selected strip, thumbnail display settings, the selection-area ref and the selection handlers.
 */
const useAfibStripEvents = ({
  activeButton,
  studyId,
  profileId,
  timezoneOffset,
  startReport,
  ecgDataMap,
}: AfibStripEventsProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const setIsLoadingStripAction = useReportStore((s) => s.actions.setIsLoadingStrip);
  /**
   * Toggles the strip grid's loading indicator. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setIsLoadingStrip = (v: Parameters<typeof setIsLoadingStripAction>[1]) =>
    setIsLoadingStripAction(keyRecoil, v);
  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Moves the strip pagination to another page. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const sizeThumbnailConfig = useReportStore((s) => s.sizeEventThumbnailConfig);
  const channelsThumbnail = useReportStore((s) => s.channelsThumbnail);
  const highPassThumbnail = useReportStore((s) => s.highPassThumbnail);
  const gainThumbnail = useReportStore((s) => s.gainThumbnail);
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  /**
   * Reads the pending event edits for the beats and HR tab. Selects the change list from the report store snapshot rather than subscribing, so the fetch routine sees edits made after it started.
   *
   * @returns The pending event changes for this tab.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Stores the strips covered by the drag selection. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Selects the strip highlighted across the beats and HR tab. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const holterEvents = useReportStore(selectHolterEvents(keyRecoil));
  const setHolterEventsAction = useReportStore((s) => s.actions.setHolterEvents);
  /**
   * Stores the page of holter events the thumbnail grid renders. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setHolterEvents = (v: Parameters<typeof setHolterEventsAction>[1]) =>
    setHolterEventsAction(keyRecoil, v);
  const getEventsInfo = useReadEventsInfoBeatHr();
  const specialEventTypes = useReportStore((s) => s.specialEventTypes);

  const setSpecialEventCountAction = useReportStore((s) => s.actions.setSpecialEventCount);
  /**
   * Stores the filtered per-type event counts that drive pagination. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setSpecialEventCount = (v: Parameters<typeof setSpecialEventCountAction>[1]) =>
    setSpecialEventCountAction(keyRecoil, v);
  const setSelectedEventInfoIndexAction = useReportStore(
    (s) => s.actions.setSelectedEventInfoIndex,
  );
  /**
   * Selects which entry of the event-info carousel is shown. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setSelectedEventInfoIndex = (v: Parameters<typeof setSelectedEventInfoIndexAction>[1]) =>
    setSelectedEventInfoIndexAction(keyRecoil, v);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);

  const [selected, setSelected] = useState(new Set<string>());
  const selectionAreaWrapperRef = useRef<SelectionAreaWrapperHandle>(null);
  const stripPosition = useRef({ x: 0, y: 0 });
  const prevPageIndex = useRef(pageIndex.index);
  const arrowKeyToPrevPage = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const cache = useRef(new Map()); // Create a cache

  let isActiveQuery: boolean;

  /**
   * Prefetches neighbouring chunks of holter events into the shared TTL cache so page turns come from memory. Merges the requested chunk ids into whatever the cache already holds, fetches each chunk with a skip and a take clipped to the total strip count, writes the results back under their chunk keys, and on failure clears the in-flight guard and logs the error.
   *
   * @param eventsFilter - the base holter-event filter each chunk request is built from.
   * @param totalStrip - total strips available, used to clip the last chunk's take.
   * @param chunkSize - how many events one cache chunk holds.
   * @param cacheChunks - the chunk indices still missing from the cache.
   * @param cacheKey - TTL cache key for this filter's chunk set.
   * @param _cacheKey - in-flight guard key, set false when the fetch fails.
   * @param lastResult - the cache entry read before this call, used when the entry has since expired.
   * @returns A promise resolving once the fetched chunks have been written to the cache.
   */
  const fetchBackgroundData = async ({
    eventsFilter,
    totalStrip,
    chunkSize,
    cacheChunks,
    cacheKey,
    _cacheKey,
    lastResult = {} as any,
  }: {
    eventsFilter: any;
    totalStrip: any;
    chunkSize: any;
    cacheChunks: any;
    cacheKey: any;
    _cacheKey: any;
    lastResult?: any;
  }) => {
    if (_.isEmpty(cacheChunks)) return;
    try {
      const getCached = ((await afibStripEventsCache.get(cacheKey)) as any) || lastResult || {};
      const stripCacheData = {
        events: getCached.events || {},
        chunks: [...(getCached?.chunks || []), ...(cacheChunks || [])],
      };
      const promisses: any[] = [];
      _.forEach(cacheChunks, (chunk: any) => {
        const skip = chunk * chunkSize;
        const take = Math.min(chunkSize, totalStrip - skip);
        if (take <= 0) {
          return;
        }
        const filter = {
          ...eventsFilter,
          skip,
        };

        promisses.push(fetchHolterEvents(filter, take, true, KEY_CANCEL.API_HOLTER_AI, false));
      });
      const results = await Promise.all(promisses);

      _.forEach(results, (result: any, idx) => {
        _.assign(stripCacheData.events, {
          [`chunk-${cacheChunks[idx]}`]: result?.events || [],
        });
      });
      afibStripEventsCache.set(cacheKey, stripCacheData);
    } catch (err) {
      cache.current.set(_cacheKey, false);
      // logError(msg, error) (handler.ts, Task 7) requires both args; this call site only passes one - pre-existing.
      (logError as any)(err);
    }
  };

  /**
   * Locates which cache chunk an absolute event index falls in. Divides the index by the chunk size and floors the result.
   *
   * @param startIndex - absolute index of the event.
   * @param chunkSize - how many events one chunk holds.
   * @returns The zero-based chunk index.
   */
  const getCurrentChunk = (startIndex: number, chunkSize: number) =>
    Math.floor(startIndex / chunkSize);

  // Get a slice of cached data
  /**
   * Cuts one page of strips out of the chunked holter-event cache. Reads the page's own chunk, appends the following one when the page straddles a chunk boundary, and slices the requested range using indices rebased onto that chunk's start.
   *
   * @param data - the cached events keyed by chunk name.
   * @param startIndex - absolute index of the page's first event.
   * @param endIndex - absolute index just past the page's last event.
   * @param currnetChunk - index of the chunk the page starts in.
   * @param chunkSize - how many events one chunk holds.
   * @returns The events covering the requested page.
   */
  const getCachedDataSlice = (
    data: any,
    startIndex: number,
    endIndex: number,
    currnetChunk: number,
    chunkSize: number,
  ) => {
    let events = data?.[`chunk-${currnetChunk}`] || [];
    const sliceChunkIdx = currnetChunk * chunkSize;
    const chunks = Math.floor(endIndex / chunkSize) - Math.floor(startIndex / chunkSize);
    if (chunks > 0) {
      const nextChunk = data?.[`chunk-${currnetChunk + 1}`] || [];
      events = [...events, ...nextChunk];
    }
    return events.slice(startIndex - sliceChunkIdx, endIndex - sliceChunkIdx);
  };

  const handleCacheData = ({
    eventsFilter,
    totalStrip,
    chunkSize,
    currentChunk,
    lastResult,
    cacheKey,
  }: {
    eventsFilter: any;
    totalStrip: any;
    chunkSize: any;
    currentChunk: any;
    lastResult: any;
    cacheKey: any;
  }) => {
    const { chunks } = lastResult || {};
    const hasCurrnetChunk = chunks?.includes(currentChunk);
    const hasNextCurrentChunk = chunks?.includes(currentChunk + 1);
    const hasPrevCurrentChunk = chunks?.includes(currentChunk - 1);
    if (hasCurrnetChunk && hasNextCurrentChunk && hasPrevCurrentChunk) {
      return;
    }
    const cacheChunks: any[] = [];
    if (!hasPrevCurrentChunk && currentChunk - 1 >= 0) {
      cacheChunks.push(currentChunk - 1);
    }
    if (!hasCurrnetChunk) {
      cacheChunks.push(currentChunk);
    }
    if (!hasNextCurrentChunk) {
      cacheChunks.push(currentChunk + 1);
    }

    const _cacheKey = `_cacheKey-${cacheKey}-${currentChunk}`;
    if (!_.isEmpty(cacheChunks) && !cache.current.has(_cacheKey)) {
      cache.current.set(_cacheKey, true);
      fetchBackgroundData({
        eventsFilter,
        totalStrip,
        chunkSize,
        cacheChunks,
        cacheKey,
        _cacheKey,
        lastResult,
      });
    }
  };

  /**
   * Loads the current page of holter events for the active rhythm type and settles the strip selection around it. Builds the type and filter request, counts special events when a filter narrows the type, serves the page from the chunked TTL cache where possible and otherwise from the API, refetches replacements for events the pending changes remove while correcting the count, then formats the strips, restores or resets the selection and clears loading.
   *
   * @returns A promise resolving once the page's strips are in the store or the fetch has failed.
   */
  const fetchEvents = async () => {
    const chunkSize = 100;
    setIsLoadingStrip(true);
    try {
      let downloadData: any[] = [];
      const type = activeButton;
      const additionalFilter = generateFilterSpecialEventType(type, specialEventTypes);
      const isAppliedFilter = isAppliedFilterEvent(eventFilter, type);

      if (isAppliedFilter) {
        _.assign(additionalFilter, {
          ...(eventFilter.isHideReviewed && { isReviewed: false }),
          ...(eventFilter.isCapture && { isCaptured: true }),
          ...(eventFilter.sortOrder &&
            (canShowCountBeatEvents.includes(type) ||
              canShowDurationEvents.includes(type) ||
              canAvgRhythmEvents.includes(type)) && {
              sortBy: eventFilter.sortBy || 'countBeats',
              sortOrder: eventFilter.sortOrder,
            }),
        });
      }

      let specialEventCount: any = 0;
      const validatePage = pageIndex.index <= 0 ? 0 : pageIndex.index;

      const eventsFilter = {
        studyId,
        profileId,
        types: [type],
        skip: validatePage * STRIP_AFIB_THUMBNAIL_INFO.stripDisplayLimit,
        ...additionalFilter,
      };

      if (!_.isEmpty(additionalFilter)) {
        const holterEventsCountFilter = {
          studyId,
          profileId,
          types: [type],
          ...additionalFilter,
        };
        specialEventCount = await fetchHolterRhythmEventsCount(
          holterEventsCountFilter,
          true,
          KEY_CANCEL.API_HOLTER_AI,
        );
      }
      const startIndex = validatePage * STRIP_AFIB_THUMBNAIL_INFO.stripDisplayLimit;
      const currentChunk = getCurrentChunk(startIndex, chunkSize);
      const keyString = JSON.stringify({ type, additionalFilter });
      const cacheKey = `afibStripEventsCache-${keyString}`;
      const currentCacheData: any = afibStripEventsCache.has(cacheKey)
        ? afibStripEventsCache.get(cacheKey)
        : {};
      const endIndex = (validatePage + 1) * STRIP_AFIB_THUMBNAIL_INFO.stripDisplayLimit;
      const isChunkOverPage =
        Math.floor(endIndex / chunkSize) - Math.floor(startIndex / chunkSize) > 0;
      const canUseCache = !isChunkOverPage || currentCacheData?.chunks?.includes(currentChunk + 1);
      const totalStrip = (getEventsInfo() as any)[(EVENT_COUNT_CONVERT as any)[type]]?.count;
      if (
        afibStripEventsCache.has(cacheKey) &&
        currentCacheData?.chunks?.includes(currentChunk) &&
        canUseCache
      ) {
        // Apply the filter before slicing the data
        const { events } = afibStripEventsCache.get(cacheKey) as any;
        const slicedData = getCachedDataSlice(
          events,
          startIndex,
          endIndex,
          currentChunk,
          chunkSize,
        );
        downloadData = [...slicedData];
      } else {
        const { events } = await fetchHolterEvents(
          eventsFilter,
          STRIP_AFIB_THUMBNAIL_INFO.stripDisplayLimit,
          true,
          KEY_CANCEL.API_HOLTER_AI,
          true,
        );
        downloadData = [...events];
      }
      if (totalStrip) {
        handleCacheData({
          eventsFilter,
          totalStrip,
          chunkSize,
          currentChunk,
          lastResult: currentCacheData,
          cacheKey,
        });
      }

      if (isAppliedFilter && downloadData.length) {
        let countRemoveInList = 0;
        let countRemoveOutsideList = 0;
        let hasRemoveEventInList = false;
        //* Nếu filter event hide review thì ko hiển thị event change từ review false thành true
        //* Nếu filter event captured thì ko hiển thị event có captured lúc đầu nhưng xoá đi
        getEventChanges().forEach((eventChange: any) => {
          if (eventChange?.type === type) {
            hasRemoveEventInList = false;
            _.remove(downloadData, (x: any) => {
              // Remove in list
              if (
                x.id === eventChange.idEvent &&
                ((eventFilter.isHideReviewed && eventChange.isReviewed) ||
                  (eventFilter.isCapture &&
                    eventChange.isUpdateImage &&
                    _.isEmpty(eventChange.stripImgs)))
              ) {
                hasRemoveEventInList = true;
                countRemoveInList += 1;
                return true;
              }
              return false;
            });
            if (
              !hasRemoveEventInList &&
              ((eventFilter.isHideReviewed && eventChange.isReviewed) ||
                (eventFilter.isCapture &&
                  eventChange.isUpdateImage &&
                  _.isEmpty(eventChange.stripImgs)))
            ) {
              // Remove outside list for correct total event count
              countRemoveOutsideList += 1;
            }
          }
        });
        if (countRemoveInList > 0) {
          const filterFetchMoreHolterEvent = {
            studyId,
            profileId,
            types: [type],
            cursor: downloadData[downloadData.length - 1].start,
            ...additionalFilter,
          };
          const { events: downloadedHolterEventsMore } = await fetchHolterEvents(
            filterFetchMoreHolterEvent,
            countRemoveInList,
            true,
            KEY_CANCEL.API_HOLTER_AI,
            false,
          );
          downloadData = [...downloadData, ...downloadedHolterEventsMore];
        }
        const formatType = (EVENT_COUNT_CONVERT as any)[type];
        specialEventCount[formatType].count -= countRemoveInList + countRemoveOutsideList;
        if (specialEventCount[formatType].count < 0) {
          specialEventCount[formatType].count = 0;
        }
      }
      if (specialEventCount) {
        setSpecialEventCount(specialEventCount);
      }

      const newHolterEvents = formatEventStrip(downloadData);

      if (isActiveQuery) {
        // selectedStripState (Recoil/index.ts, Task 7) is typed IEvent | IBeat | IEcgBookmark, but
        // IEcgBookmark/IBeat don't declare isNew/idEvent, which this afib-events flow always reads - pre-existing.
        if (!(selectedStrip as any)?.isNew) {
          if (validatePage < prevPageIndex.current && arrowKeyToPrevPage.current) {
            stripPosition.current = getStripPosition(
              newHolterEvents.length - 1,
              STRIP_AFIB_THUMBNAIL_INFO.stripPerRow,
            );
            setSelectedStrip((newHolterEvents[newHolterEvents.length - 1] || null) as any);
          } else {
            const selectedStripIndex = !isAppliedFilter
              ? _.findIndex(newHolterEvents, (x) => x.idEvent === (selectedStrip as any)?.idEvent)
              : -1;
            const newSelectedStrip =
              selectedStripIndex === -1
                ? newHolterEvents[0] || null
                : newHolterEvents[selectedStripIndex];
            stripPosition.current = getStripPosition(
              selectedStripIndex === -1 ? 0 : selectedStripIndex,
              STRIP_AFIB_THUMBNAIL_INFO.stripPerRow,
            );
            setSelectedStrip((prev: any) => {
              if (!_.isEqual(newSelectedStrip, prev)) {
                return newSelectedStrip;
              }
              return prev;
            });
          }
          arrowKeyToPrevPage.current = false;
          prevPageIndex.current = validatePage;
        }
        setHolterEvents(newHolterEvents as any);
        setIsLoadingStrip(false);
      }
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    }
  };

  // Handle drag select
  /**
   * Records which strip thumbnails the drag selection currently covers. Replaces the local set of selected ids, which an effect then maps back onto loaded holter events.
   *
   * @param newSelected - the set of selected strip element ids.
   * @returns Nothing.
   */
  const setSelectedEvent = (newSelected: Set<string>) => {
    setSelected(newSelected);
  };

  useEffect(() => {
    const selectedArr = [...selected];
    const selectedEvents: any[] = [];
    _.forEach(selectedArr, (item) => {
      const id = item.split('-')[1];
      /**
       * Resolves one drag-selected strip id back to the holter event behind it. Matches the numeric id parsed out of the selection key against the events loaded for the current page.
       *
       * @returns The matching holter event, or undefined when the page holds none.
       */
      const selectedEvent = (holterEvents || []).find((event) => event.id === Number(id));
      if (selectedEvent) {
        selectedEvents.push(selectedEvent);
      }
    });
    setSelectedMultipleStrip((prev: any) => {
      if (prev.length === 0 && selectedEvents.length === 0) {
        return prev;
      }
      return selectedEvents;
    });
  }, [selected, holterEvents]);

  /**
   * Selects the strip thumbnail the user clicked. Resets the event-info carousel to its first entry and, when the clicked event differs from the current selection, records the thumbnail's row and column position before storing it.
   *
   * @param event - the holter event behind the clicked thumbnail.
   * @param index - the thumbnail's position within the rendered page.
   * @returns Nothing.
   */
  const handleClickSelectEvent = useCallback((event: any, index: any) => {
    setSelectedEventInfoIndex(0);
    setSelectedStrip((prev: any) => {
      if (event?.id !== prev?.id) {
        stripPosition.current = getStripPosition(index, STRIP_AFIB_THUMBNAIL_INFO.stripPerRow);
        return event;
      }
      return prev;
    });
  }, []);

  useEffect(
    () => () => {
      storeTimeout.removeStore(activeButton, timeoutRef.current);
    },
    [activeButton],
  );

  /**
   * Drops every cached page of strip events. Clears both the shared TTL cache and the per-hook in-flight guard map, so the next fetch goes back to the server.
   *
   * @returns Nothing.
   */
  const resetCacheStripEvents = () => {
    afibStripEventsCache.clear();
    cache.current.clear();
  };

  useEffect(() => {
    resetCacheStripEvents();
  }, [isRenderViewerEcg]);

  useEffect(() => {
    isActiveQuery = true;
    if (isRenderViewerEcg) {
      fetchEvents();
    }
    if (isActiveTab) {
      setSelected(new Set());
      selectionAreaWrapperRef.current.resetSelection();
    }
    return () => {
      isActiveQuery = false;
    };
  }, [activeButton, eventFilter, pageIndex, isActiveTab, isRenderViewerEcg]);

  useEffect(() => {
    /**
     * Moves the strip selection around the thumbnail grid with the arrow keys. Ignores auto-repeat and presses made while strips are loading or while the pointer sits outside the beats and HR tab zone, then clears the drag selection and steps the row or column, selecting the event at the new position, reverting the step when no event sits there, and emitting the next or previous page event at the row's edges.
     *
     * @param event - the window keydown event.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (_.isEmpty(selectionAreaWrapperRef.current) || event?.repeat || isLoadingStrip) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      event.preventDefault();
      const { key } = event;
      switch (key) {
        case 'ArrowRight': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y += 1;
          const index = getStripIndex(stripPosition.current, STRIP_AFIB_THUMBNAIL_INFO.stripPerRow);
          if (index > holterEvents.length - 1) {
            stripPosition.current.y -= 1;
            emitter.emit(EMITTER_CONSTANTS.BEAT_HR_NEXT);
          } else {
            const event = holterEvents[index];
            if (event) {
              setSelectedStrip(event);
            } else {
              stripPosition.current.y -= 1;
            }
          }
          break;
        }
        case 'ArrowLeft': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y -= 1;
          const index = getStripIndex(stripPosition.current, STRIP_AFIB_THUMBNAIL_INFO.stripPerRow);
          if (index < 0) {
            arrowKeyToPrevPage.current = true;
            stripPosition.current.y += 1;
            emitter.emit(EMITTER_CONSTANTS.BEAT_HR_PREV);
          } else {
            const event = holterEvents[index];
            if (event) {
              setSelectedStrip(event);
            } else {
              stripPosition.current.y += 1;
            }
          }
          break;
        }
        case 'ArrowUp': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x -= 1;
          const event =
            holterEvents[
              getStripIndex(stripPosition.current, STRIP_AFIB_THUMBNAIL_INFO.stripPerRow)
            ];
          if (event) {
            setSelectedStrip(event);
          } else {
            stripPosition.current.x += 1;
          }
          break;
        }
        case 'ArrowDown': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x += 1;
          const event =
            holterEvents[
              getStripIndex(stripPosition.current, STRIP_AFIB_THUMBNAIL_INFO.stripPerRow)
            ];
          if (event) {
            setSelectedStrip(event);
          } else {
            stripPosition.current.x -= 1;
          }
          break;
        }
        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [holterEvents, isLoadingStrip]);

  const isHasSelected = useMemo(
    () => !_.isEmpty(selectedMultipleStrip) || !_.isEmpty(selectedStrip),
    [selectedMultipleStrip, selectedStrip],
  );

  useUpdateEffect(() => {
    const { x: row, y: column } = stripPosition.current;
    const validatePage = pageIndex.index <= 0 ? 0 : pageIndex.index;
    const currentStripIndexInList = column + row * STRIP_AFIB_THUMBNAIL_INFO.prevStripPerRow;
    const currentEventIndex =
      currentStripIndexInList + validatePage * STRIP_AFIB_THUMBNAIL_INFO.prevStripDisplayLimit;
    const newPage = Math.floor(currentEventIndex / STRIP_AFIB_THUMBNAIL_INFO.stripDisplayLimit);
    setPageIndex({ index: newPage < 0 ? 0 : newPage });
  }, [sizeThumbnailConfig]);

  return {
    keyRecoil,
    activeButton,
    timezoneOffset,
    startReport,
    ecgDataMap,
    isLoadingStrip,
    sizeThumbnailConfig,
    channelsThumbnail,
    highPassThumbnail,
    gainThumbnail,
    selectedStrip,
    holterEvents,
    selectionAreaWrapperRef,
    setSelectedEvent,
    handleClickSelectEvent,
    isHasSelected,
  };
};

export default useAfibStripEvents;
